import React, { useEffect, useState, useMemo } from "react";
import EditProfileModal from "../auth/editAccount";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { collection, getDocs, query, where, doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import UploadPolicyModal from "../renewals/UploadPolicy";
import UnifiedPolicyForm from "../addPolicy/UnifiedPolicyForm";
import { incrementDailyPolicyCounter } from "../../utils/dailyPolicyCounter";
import UploadDocumentModal from "../../components/UploadDocumentModal";

// MUI UI shell while preserving behavior
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Pagination from "@mui/material/Pagination";
import Link from "@mui/material/Link";
import useMediaQuery from "@mui/material/useMediaQuery";
import StatusBadge from "../../components/common/StatusBadge";
import { calculatePolicyStatus } from "../../utils/dateUtils";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { getPolicyCountsByCategory } from "../../utils/countUtils";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import FlagIcon from '@mui/icons-material/Flag';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { fetchClientById } from "../clients/clientSlice";
import { clearCurrentPolicy } from "../policies/policySlice";
import { firebaseCache } from "../../utils/firebaseCache";
import { trashDriveFile, getDriveViewUrl } from "../../utils/driveApi";
import { deleteObject, ref } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";
import { useAdminStaffData } from "../../hooks/useAdminStaffData";
import { useAccountStatus } from "../../hooks/useAccountStatus";
import { resolveBranchName } from "../../utils/branchNameResolver";

const ClientDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  const { user } = useSelector((state) => state.auth);
  const { clients } = useSelector((state) => state.clients);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
  const { isAccountActive } = useAccountStatus();

  // Get the page and tab we came from
  const fromPage = location.state?.fromPage || 1;
  const fromTab = location.state?.fromTab || 'customers';
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("motor");
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePolicyDialogOpen, setDeletePolicyDialogOpen] = useState(false);
  const [deleteDocDialogOpen, setDeleteDocDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const [editData, setEditData] = useState({
    name: location.state?.client?.name || "",
    phone: location.state?.client?.mobile || location.state?.client?.phone || "",
    email: location.state?.client?.email || "",
    location: location.state?.client?.address || ""
  });
  const [customerData, setCustomerData] = useState(location.state?.client || null);

  // Upload policy states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [docUploadProgress, setDocUploadProgress] = useState(0);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [prefillData, setPrefillData] = useState(null);

  // Document Menu state
  const [docMenuAnchor, setDocMenuAnchor] = useState(null);
  const isDocMenuOpen = Boolean(docMenuAnchor);

  const categories = ["motor", "non-motor", "life"]; // aligns with database structure
  // Responsive rows per page based on viewport height
  const veryShortScreen = useMediaQuery('(max-height:700px)');
  const shortScreen = useMediaQuery('(max-height:850px)');
  const mediumScreen = useMediaQuery('(max-height:1000px)');
  const itemsPerPage = veryShortScreen ? 4 : shortScreen ? 5 : mediumScreen ? 6 : 8;
  const [currentPage, setCurrentPage] = useState(1);
  const [viewingDocId, setViewingDocId] = useState(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [policyViewType, setPolicyViewType] = useState('current');
  const isMobile = useMediaQuery('(max-width:768px)');
  const isTablet = useMediaQuery('(max-width:1024px)');
  const isSmallScreen = useMediaQuery('(max-width:1281px)');

  // Function to determine the best default category based on available policies
  const determineDefaultCategory = (policies) => {
    if (!policies || policies.length === 0) return "motor";

    // Check which categories have policies
    const hasMotor = policies.some(p => p.policyCategory === "motor");
    const hasNonMotor = policies.some(p => p.policyCategory === "non-motor");
    const hasLife = policies.some(p => p.policyCategoryType === "life");

    // Priority: motor > non-motor > life
    if (hasMotor) return "motor";
    if (hasNonMotor) return "non-motor";
    if (hasLife) return "life";

    return "motor"; // fallback
  };

  const fetchPoliciesAndCustomer = async (showLoading = true) => {
    if (!user?.uid || !customerId || staffLoading) {
      console.log(`⏳ ClientDetail: Skipping fetch - user: ${!!user?.uid}, customerId: ${!!customerId}, staffLoading: ${staffLoading}`);
      return;
    }

    try {
      if (showLoading) setLoading(true);

      // Determine the correct agentId for this client
      // 1. Try from navigation state
      // 2. Try to find in the already loaded clients list
      // 3. Fallback to current user UID (only if not admin)
      let effectiveAgentId = location.state?.client?.agentId || location.state?.agentId;

      if (!effectiveAgentId && isAdmin && userIds.length > 0) {
        console.log(`🔍 Admin: Searching for customer ${customerId} across ${userIds.length} staff members...`);

        // Check our current clients list from Redux
        const clientInState = clients.find(c => c.id === customerId);
        if (clientInState?.agentId) {
          effectiveAgentId = clientInState.agentId;
          console.log(`✅ Found customer in state, agentId: ${effectiveAgentId}`);
        } else {
          // Search across all staff/admin collections to find where this customer exists
          console.log(`🔍 Admin: Customer not in state, searching across ${userIds.length} agents...`);
          for (const uid of userIds) {
            try {
              const customerRef = doc(db, `agents/${uid}/customers/${customerId}`);
              const customerSnap = await getDoc(customerRef);
              if (customerSnap.exists()) {
                effectiveAgentId = uid;
                console.log(`✅ Found customer in agents/${uid}/customers/${customerId}`);
                break;
              }
            } catch (err) {
              console.warn(`Could not check agents/${uid}/customers/${customerId}:`, err.message);
            }
          }

          if (!effectiveAgentId) {
            console.warn(`⚠️ Admin: Could not find customer ${customerId} in any agent collection. Using first userId as fallback.`);
            effectiveAgentId = userIds[0];
          }
        }
      } else if (!effectiveAgentId) {
        effectiveAgentId = user.uid;
      }

      console.log(`🔄 ClientDetail: Fetching for agentId: ${effectiveAgentId}, customerId: ${customerId}`);

      // Use Redux actions for optimized fetching with caching
      const fetchResult = await dispatch(fetchClientById({ agentId: effectiveAgentId, customerId })).unwrap();

      // Fetch policies for this specific customer using optimized query
      const basePath = `agents/${effectiveAgentId}/policies`;
      const q = query(collection(db, basePath), where("customerId", "==", customerId));
      const snapshot = await getDocs(q);
      console.log(`📋 ClientDetail: Found ${snapshot.docs.length} policies for customer ${customerId}`);
      const fetchedPolicies = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setPolicies(fetchedPolicies);
      console.log(`✅ ClientDetail: Set ${fetchedPolicies.length} policies in state`);

      // Auto-select the best category based on available policies
      const bestCategory = determineDefaultCategory(fetchedPolicies);
      setSelectedCategory(bestCategory);

      // Get customer data from Redux state if available, otherwise fallback to Firebase
      // Use the fetchResult from unwrap() to ensure we have the absolute latest data correctly
      const client = fetchResult || clients.find(c => c.id === customerId);
      if (client) {
        console.log(`📄 Document check: RC count: ${client.documents?.rc_book?.length || 0}, Aadhaar count: ${client.documents?.aadhaar?.length || 0}`);
        setCustomerData(client);
        setEditData({
          name: client.name || "",
          phone: client.mobile || "",
          email: client.email || "",
          location: client.address || "",
          referredBy: client.referredBy || ""
        });

        // Check for missing Drive documents
        if (client.documents) {
          checkMissingDocuments(effectiveAgentId, customerId, client.documents);
        }
      } else {
        // Fallback to direct Firebase query
        const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);
        const customerSnap = await getDoc(customerRef);
        if (customerSnap.exists()) {
          const c = customerSnap.data();
          // IMPORTANT: Include agentId so document uploads go to the correct path
          setCustomerData({ id: customerSnap.id, ...c, agentId: effectiveAgentId });
          setEditData({
            name: c.name || "",
            phone: c.mobile || "",
            email: c.email || "",
            location: c.address || "",
            referredBy: c.referredBy || ""
          });

          // Check for missing Drive documents
          if (c.documents) {
            checkMissingDocuments(effectiveAgentId, customerSnap.id, c.documents);
          }
        }
      }
    } catch (err) {
      console.error("🔥 Error in fetchPoliciesAndCustomer:", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if any Drive-stored documents are missing and remove them
   */
  const checkMissingDocuments = async (agentId, clientId, documents) => {
    try {
      if (!documents) return;

      const driveDocIds = [];
      const driveDocInfo = []; // Track which type and ID each file corresponds to

      // Collect all Drive file IDs
      Object.entries(documents).forEach(([type, docs]) => {
        const docList = Array.isArray(docs) ? docs : [docs];
        docList.forEach(doc => {
          if (doc.pdfSource === 'drive' && doc.driveFileId) {
            driveDocIds.push(doc.driveFileId);
            driveDocInfo.push({ type, id: doc.id || doc.driveFileId, driveFileId: doc.driveFileId });
          }
        });
      });

      if (driveDocIds.length === 0) return;

      console.log(`🔍 Checking existence for ${driveDocIds.length} Drive documents...`);
      const { checkFilesExistence } = await import('../../utils/driveApi');
      const existenceMap = await checkFilesExistence(driveDocIds);

      const missingDocs = driveDocInfo.filter(info => existenceMap[info.driveFileId] === false);

      if (missingDocs.length > 0) {
        console.warn(`⚠️ Found ${missingDocs.length} missing Drive documents. Syncing...`);

        // Prepare updated documents object
        const updatedDocuments = JSON.parse(JSON.stringify(documents));
        let changed = false;

        missingDocs.forEach(missing => {
          const type = missing.type;
          if (Array.isArray(updatedDocuments[type])) {
            updatedDocuments[type] = updatedDocuments[type].filter(d => (d.id || d.driveFileId) !== missing.id);
            if (updatedDocuments[type].length === 0) delete updatedDocuments[type];
            changed = true;
          } else if (updatedDocuments[type] && (updatedDocuments[type].id || updatedDocuments[type].driveFileId) === missing.id) {
            delete updatedDocuments[type];
            changed = true;
          }
        });

        if (changed) {
          // Update Firestore
          const { updateDoc, doc } = await import("firebase/firestore");
          const customerRef = doc(db, `agents/${agentId}/customers/${clientId}`);
          await updateDoc(customerRef, { documents: updatedDocuments });

          // Update local state
          setCustomerData(prev => ({ ...prev, documents: updatedDocuments }));

          toast.info(`${missingDocs.length} document(s) no longer exist in Drive and have been removed from your list.`, {
            autoClose: 5000
          });

          // Invalidate cache
          firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
        }
      }
    } catch (error) {
      console.error("❌ Error auto-syncing missing documents:", error);
    }
  };

  useEffect(() => {
    console.log(`🔄 ClientDetail: useEffect triggered - user: ${user?.uid}, customerId: ${customerId}, staffLoading: ${staffLoading}`);
    if (!staffLoading) {
      fetchPoliciesAndCustomer();
    }
  }, [user?.uid, customerId, dispatch, staffLoading]);

  // Filter the policies based on current vs previous
  const statusFilteredPolicies = useMemo(() => {
    return policies.filter(p => {
      const status = calculatePolicyStatus(p.expiryDate, p.status);
      return policyViewType === 'current' ? status !== 'Expired' : status === 'Expired';
    });
  }, [policies, policyViewType]);

  // Update selected category when policies or policyViewType changes
  useEffect(() => {
    if (statusFilteredPolicies.length > 0) {
      const bestCategory = determineDefaultCategory(statusFilteredPolicies);
      if (bestCategory !== selectedCategory) {
        setSelectedCategory(bestCategory);
      }
    }
  }, [statusFilteredPolicies]);

  // Calculate counts for each category for this client based on status filter
  const categoryCounts = useMemo(() => {
    return getPolicyCountsByCategory(statusFilteredPolicies);
  }, [statusFilteredPolicies]);

  // Calculate counts for status types
  const currentCount = useMemo(() => policies.filter(p => calculatePolicyStatus(p.expiryDate, p.status) !== 'Expired').length, [policies]);
  const previousCount = useMemo(() => policies.filter(p => calculatePolicyStatus(p.expiryDate, p.status) === 'Expired').length, [policies]);

  const clientName = customerData?.name || editData.name || "Client";
  const clientPhone = customerData?.mobile || editData.phone || "Phone";

  const handleSaveCustomer = async (updatedData) => {
    try {
      const effectiveAgentId = customerData?.agentId || user?.uid;
      const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);
      // Map the form data to the correct field names for customer
      const updatedPayload = {
        name: updatedData.name,
        mobile: updatedData.phone,
        email: updatedData.email || "",
        address: updatedData.location,
        referredBy: updatedData.referredBy || "",
        updatedAt: new Date().toISOString()
      };
      await setDoc(customerRef, updatedPayload, { merge: true });
      setCustomerData({ ...customerData, ...updatedPayload });
      setEditData(updatedPayload);
      setShowEditModal(false);

      // Update all policies associated with this customer
      try {
        const policiesRef = collection(db, `agents/${effectiveAgentId}/policies`);
        const q = query(policiesRef, where("customerId", "==", customerId));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const batch = (await import("firebase/firestore")).writeBatch(db);
          snapshot.docs.forEach(docSnap => {
            const policyRef = doc(db, `agents/${effectiveAgentId}/policies/${docSnap.id}`);
            batch.update(policyRef, {
              name: updatedData.name,
              mobile: updatedData.phone,
              email: updatedData.email || "",
              address: updatedData.location,
              referredBy: updatedData.referredBy || "",
              updatedAt: new Date().toISOString()
            });
          });
          await batch.commit();
          console.log(`✅ Updated ${snapshot.docs.length} policies with new customer information`);
        }
      } catch (policyError) {
        console.error("❌ Error updating policies:", policyError);
        // Don't fail the entire operation if policy updates fail
      }

      // Mark client cache for invalidation (ClientDetails write operation)
      firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
      // Also invalidate policy cache since we updated policy documents
      firebaseCache.invalidateAfterWrite('policydetails_write', user?.uid);
      dispatch(clearCurrentPolicy());
      console.log("✅ Client and policy caches marked for invalidation after profile update");

      // Add a small delay to ensure the modal is closed before showing toast
      setTimeout(() => {
        console.log("🎉 Customer profile update success - showing toast");
        toast.success("Customer profile successfully updated", {
          containerId: "bottom-center",
          position: "bottom-center",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          closeButton: false,
          style: {
            backgroundColor: '#DCFCE7',
            color: '#FFFFFF',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            padding: '12px 16px',
            minHeight: '44px',
            alignItems: 'center',
            gap: '2px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            marginBottom: '75vh',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 'auto',
            maxWidth: 'none',
            display: 'inline-flex',
          },
          icon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: '#FFFFFF' }}
            >
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        });
      }, 100);
    } catch (error) {
      console.error("Failed to update customer:", error);
      toast.error("Failed to update customer details");
    }
  };

  // Handle successful policy upload
  const handlePolicyUploadSuccess = async () => {
    console.log("🎉 Policy upload success - refreshing policies");
    setShowPolicyForm(false);
    setPrefillData(null);

    // Increment daily policy counter after successful save
    try {
      incrementDailyPolicyCounter(user?.uid);
      console.log('📊 Daily policy counter incremented after successful client policy upload');
    } catch (error) {
      console.error('❌ Error incrementing daily policy counter:', error);
    }

    // Mark cache for invalidation (ClientDetails write operation)
    firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
    console.log("✅ Client cache marked for invalidation after policy upload");

    // Refetch policies to update the list
    await fetchPoliciesAndCustomer();

    // Show success toast
    toast.success("Policy successfully added", {
      containerId: "bottom-center",
      position: "bottom-center",
      autoClose: 2000,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      closeButton: false,
      style: {
        backgroundColor: '#DCFCE7',
        color: '#FFFFFF',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: 500,
        padding: '12px 16px',
        minHeight: '44px',
        alignItems: 'center',
        gap: '2px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        marginBottom: '75vh',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: 'auto',
        maxWidth: 'none',
        display: 'inline-flex',
      },
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ color: '#FFFFFF' }}
        >
          <path
            d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    });
  };



  const filteredPolicies = statusFilteredPolicies.filter((p) => {
    if (selectedCategory === "motor") return p.policyCategory === "motor";
    if (selectedCategory === "non-motor") return p.policyCategory === "non-motor";
    return p.policyCategoryType === selectedCategory;
  });

  const paginated = filteredPolicies.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredPolicies.length / itemsPerPage) || 1;

  // Delete customer and related policies
  const handleDeleteCustomer = async () => {
    if (!user?.uid || !customerId) return;
    try {
      setIsActionLoading(true); // Set action loading state
      const effectiveAgentId = customerData?.agentId || user?.uid;
      // Delete customer document
      const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);
      await (await import("firebase/firestore")).deleteDoc(customerRef);

      // Delete all related policies and their PDF files
      const policiesRef = collection(db, `agents/${effectiveAgentId}/policies`);
      const q = query(policiesRef, where("customerId", "==", customerId));
      const snapshot = await getDocs(q);

      // Delete PDF files from Firebase Storage first
      const { deleteObject, ref } = await import("firebase/storage");
      const { storage } = await import("../../firebase/firebaseConfig");

      const deletePromises = [];
      snapshot.docs.forEach(docSnap => {
        const policyData = docSnap.data();
        // If policy has a PDF URL, delete the file from storage
        if (policyData.pdfUrl) {
          try {
            // Extract the file path from the URL
            const url = new URL(policyData.pdfUrl);
            const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
            if (pathMatch) {
              const filePath = decodeURIComponent(pathMatch[1]);
              const fileRef = ref(storage, filePath);
              deletePromises.push(deleteObject(fileRef));
            }
          } catch (error) {
            console.warn("Could not parse PDF URL for deletion:", policyData.pdfUrl, error);
          }
        }
      });

      // Wait for all PDF deletions to complete
      if (deletePromises.length > 0) {
        await Promise.allSettled(deletePromises);
        console.log(`Attempted to delete ${deletePromises.length} PDF files`);
      }

      // Delete policy documents from Firestore
      const batch = (await import("firebase/firestore")).writeBatch(db);
      snapshot.docs.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
      await batch.commit();

      // Mark cache for invalidation (ClientDetails delete operation)
      firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
      console.log("✅ Client cache marked for invalidation after delete");

      // Show success toast before navigation
      toast.success("Client has been successfully deleted", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });

      // Navigate back to the tab we came from after a brief delay
      setTimeout(() => {
        navigate('/client', {
          state: {
            currentPage: fromPage,
            selectedTab: fromTab,
            forceRefetch: true // Force refetch to ensure deleted client is removed from list
          }
        });
      }, 500);
    } catch (err) {
      console.error("Error deleting customer and policies:", err);
      toast.error("Failed to delete client. Please try again.");
    } finally {
      setIsActionLoading(false);
    }
  };

  // Delete individual policy and its PDF
  const handleDeletePolicy = async (policy) => {
    if (!user?.uid || !policy?.id) return;

    try {
      setIsActionLoading(true);
      // Handle file deletion based on storage source
      if (policy.pdfSource === "drive" && policy.driveFileId) {
        // For Google Drive-stored policies: DO NOT delete the Drive file automatically
        // Just inform the user that the document remains in Drive
        console.log("📁 Drive-stored policy - document will remain in Google Drive");
        // Optional: If we want to add a checkbox to trash the Drive file, we can add that later
      } else if (policy.pdfUrl) {
        // For Firebase Storage-stored policies: delete the PDF file
        try {
          const { deleteObject, ref } = await import("firebase/storage");
          const { storage } = await import("../../firebase/firebaseConfig");

          // Extract the file path from the URL
          const url = new URL(policy.pdfUrl);
          const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
          if (pathMatch) {
            const filePath = decodeURIComponent(pathMatch[1]);
            const fileRef = ref(storage, filePath);
            await deleteObject(fileRef);
            console.log("✅ PDF file deleted from Firebase Storage");
          }
        } catch (error) {
          console.warn("Could not delete PDF file from Firebase:", error);
          // Continue with policy deletion even if PDF deletion fails
        }
      }

      // Delete policy document from Firestore
      const effectiveAgentId = customerData?.agentId || user?.uid;
      const policyRef = doc(db, `agents/${effectiveAgentId}/policies/${policy.id}`);
      await (await import("firebase/firestore")).deleteDoc(policyRef);

      // Update local policies state
      setPolicies(prevPolicies => prevPolicies.filter(p => p.id !== policy.id));

      // Mark cache for invalidation (ClientDetails policy delete operation)
      firebaseCache.invalidateAfterWrite('clientdetails_write', user?.uid);
      dispatch(clearCurrentPolicy());
      console.log("✅ Client cache marked for invalidation after policy delete");

      // Show appropriate success toast based on storage source
      const isDrivePolicy = policy.pdfSource === "drive" && policy.driveFileId;
      const successMessage = isDrivePolicy
        ? "Policy removed from PolicyTrackr. Document remains in your Google Drive."
        : "Policy has been successfully deleted";

      toast.success(successMessage, {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: isDrivePolicy ? 4000 : 2000, // Longer display for Drive message
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: isDrivePolicy ? '#DBEAFE' : '#DCFCE7', // Blue for Drive, green for Firebase
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: isDrivePolicy ? (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });

    } catch (err) {
      console.error("Error deleting policy:", err);
      toast.error("Failed to delete policy. Please try again.");
    } finally {
      setIsActionLoading(false);
    }
  };

  // Process rows with status calculation
  const rows = paginated.map((p, index) => {
    return {
      id: p.id || index,
      name: clientName,
      mobile: clientPhone,
      policyCategoryType: p.policyCategoryType,
      vehicleNumber: p.vehicleNumber,
      policyType: p.policyType,
      policyCompany: p.policyCompany,
      status: calculatePolicyStatus(p.expiryDate, p.status),
      original: p
    };
  });

  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  const renderDocumentList = (type, title) => {
    const rawData = customerData?.documents?.[type];
    if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="textSecondary">Not uploaded</Typography>
      </Box>
    );

    const docList = Array.isArray(rawData) ? rawData : [rawData];

    return (
      <Box sx={{ mt: 1, width: '100%' }}>
        {docList.map((doc, idx) => (
          <Box
            key={doc.id || idx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
              borderTop: idx > 0 ? '1px solid #f0f0f0' : 'none'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
              <DescriptionIcon sx={{ fontSize: 16, color: '#666' }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  color: '#444',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '120px'
                }}
                title={doc.fileName || `Document ${idx + 1}`}
              >
                {doc.fileName || `Document ${idx + 1}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleViewDocument(doc)}
                sx={{ color: '#0063F7' }}
                title="View"
                disabled={viewingDocId === (doc.driveFileId || doc.pdfUrl)}
              >
                {viewingDocId === (doc.driveFileId || doc.pdfUrl) ? (
                  <CircularProgress size={18} thickness={5} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setDocToDelete({ type, ...doc });
                  setDeleteDocDialogOpen(true);
                }}
                sx={{ color: '#DC2626' }}
                title="Delete"
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const handleDocumentUpload = async (file, type) => {
    if (!file || !type) return;

    try {
      setIsActionLoading(true);
      setDocUploadProgress(0);
      const adminUid = customerData?.adminUid || user?.uid;

      // Compress PDF before uploading
      let uploadFile = file;
      if (file.type === 'application/pdf') {
        try {
          setDocUploadProgress(5);
          const { compressPdf } = await import('../../utils/pdfCompressor');
          uploadFile = await compressPdf(file);
          if (uploadFile.size < file.size) {
            console.log(`📦 Document compressed: ${((file.size - uploadFile.size) / 1024).toFixed(0)}KB saved`);
          }
        } catch (compError) {
          console.error('Document compression failed, using original:', compError);
        }
      }
      setDocUploadProgress(10);

      const driveApi = await import('../../utils/driveApi');
      const driveStatus = await driveApi.checkDriveStatus();
      const driveLinked = driveStatus.linked;

      let uploadResult = {};
      const timestamp = new Date().getTime();
      const extension = file.name.split('.').pop() || 'file';
      const fileName = `${type}_${timestamp}.${extension}`;

      // Use the resolved agentId or fallback to what we have
      const effectiveAgentId = customerData?.agentId || location.state?.agentId || user?.uid;

      console.log(`📡 [handleDocumentUpload] Target Agent ID: ${effectiveAgentId}, User: ${user?.uid}, Doc Type: ${type}`);

      if (driveLinked) {
        // Upload to Drive - Simulate progress for Drive API call
        setDocUploadProgress(20);
        const isAdminCheck = user?.isPhoneUser || false;
        // Use the branch name resolver
        const branchName = await resolveBranchName(user, effectiveAgentId, isAdminCheck);

        if (!branchName && !isAdminCheck) {
          toast.error("Please add a Branch Name in your profile before uploading to Google Drive.", { autoClose: 5000 });
          throw new Error("Missing branch name for Drive upload");
        }

        setDocUploadProgress(40);
        setDocUploadProgress(60);
        const driveSubFolder = type === 'rc_book' ? 'Documents/RC' : 'Documents/Aadhar';
        const referredBy = customerData?.referredBy || editData?.referredBy || '';
        const clientFolderName = referredBy ? `${clientName} (Ref: ${referredBy})` : clientName;
        const data = await driveApi.uploadToDrive(uploadFile, branchName, clientFolderName, null, driveSubFolder, fileName);

        setDocUploadProgress(100);
        uploadResult = {
          pdfSource: 'drive',
          driveFileId: data.fileId,
          pdfUrl: data.webViewLink, // Store view URL as fallback
          updatedAt: new Date().toISOString()
        };
      } else {
        // Enforce Google Drive - do not allow Firebase Storage uploads for documents
        const errorMsg = "Google drive not linked by admin, pls contact admin";
        toast.error(errorMsg, {
          autoClose: 5000,
          containerId: "bottom-center",
          position: "bottom-center"
        });
        throw new Error("Drive not linked by admin");
      }

      // Update Firestore customer document
      const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);

      // Ensure we handle currentDocuments as an array for multiple uploads
      let currentDocs = customerData?.documents?.[type] || [];
      // Support legacy data where document might be a single object instead of array
      if (currentDocs && !Array.isArray(currentDocs)) {
        currentDocs = [currentDocs];
      }

      const updatedList = [...currentDocs, { ...uploadResult, id: timestamp, fileName: file.name }];

      const updatedDocuments = {
        ...(customerData?.documents || {}),
        [type]: updatedList
      };

      console.log(`💾 [handleDocumentUpload] Updating Firestore at agents/${effectiveAgentId}/customers/${customerId}`);
      await setDoc(customerRef, { documents: updatedDocuments }, { merge: true });

      setCustomerData({
        ...customerData,
        documents: updatedDocuments
      });

      // Update Redux state local copy - force refresh to bypass cache
      dispatch(fetchClientById({ agentId: effectiveAgentId, customerId, forceRefresh: true }));

      // Clear the progress and close modal before showing toast
      setDocUploadProgress(0);
      setShowUploadDocModal(false);

      // Show stylized success toast
      toast.success("Customer document successfully added", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    } catch (error) {
      console.error("❌ Document upload error:", error);
      toast.error(`Document upload failed: ${error.message}`);
      setDocUploadProgress(0);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeleteDocument = async (type, docId) => {
    try {
      setIsActionLoading(true);
      const currentDocs = customerData?.documents?.[type];
      if (!currentDocs) return;

      const docList = Array.isArray(currentDocs) ? currentDocs : [currentDocs];
      const docToDelete = docList.find(d => d.id === docId || d.driveFileId === docId || d.pdfUrl === docId);

      if (!docToDelete) {
        toast.error("Document not found");
        return;
      }

      if (docToDelete.pdfSource === 'drive') {
        try {
          await trashDriveFile(docToDelete.driveFileId);
        } catch (driveErr) {
          console.warn("⚠️ Could not delete from Drive (link might be broken), proceeding with Firestore update:", driveErr.message);
        }
      } else if (docToDelete.pdfUrl) {
        // Extract the file path from the URL
        const url = new URL(docToDelete.pdfUrl);
        const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
        if (pathMatch) {
          const filePath = decodeURIComponent(pathMatch[1]);
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef).catch(err => console.error("Error deleting file from storage:", err));
        }
      }

      // Update Firestore
      const effectiveAgentId = customerData?.agentId || location.state?.agentId || user?.uid;
      console.log(`📡 [handleDeleteDocument] Target Agent ID: ${effectiveAgentId}, User: ${user?.uid}, Doc Type: ${type}`);
      const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${customerId}`);

      const updatedList = docList.filter(d => d.id !== docId && d.driveFileId !== docId && d.pdfUrl !== docId);

      const updatedDocuments = { ...customerData.documents };
      if (updatedList.length === 0) {
        delete updatedDocuments[type];
      } else {
        updatedDocuments[type] = updatedList;
      }

      await setDoc(customerRef, { documents: updatedDocuments }, { merge: true });

      setCustomerData({
        ...customerData,
        documents: updatedDocuments
      });

      // Update Redux state to keep everything in sync
      dispatch(fetchClientById({ agentId: customerData?.agentId || user?.uid, customerId }));

      toast.success("Customer document successfully removed", {
        containerId: "bottom-center",
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        closeButton: false,
        style: {
          backgroundColor: '#DCFCE7',
          color: '#FFFFFF',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
          minHeight: '44px',
          alignItems: 'center',
          gap: '2px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginBottom: '75vh',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: 'auto',
          maxWidth: 'none',
          display: 'inline-flex',
        },
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#FFFFFF' }}
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleViewDocument = async (docData) => {
    if (!docData || viewingDocId) return;

    const docId = typeof docData === 'string' ? docData : (docData.driveFileId || docData.pdfUrl);
    setViewingDocId(docId);

    try {
      // Handle legacy string URLs
      if (typeof docData === 'string') {
        window.open(docData, '_blank');
        return;
      }

      if (docData.pdfSource === 'drive') {
        try {
          const viewUrl = await getDriveViewUrl(docData.driveFileId);
          if (viewUrl) {
            window.open(viewUrl, '_blank');
          } else if (docData.pdfUrl) {
            // Fallback to stored URL if API fails but we have a URL
            window.open(docData.pdfUrl, '_blank');
          } else {
            toast.error("Could not get Drive view URL");
          }
        } catch (error) {
          console.error("❌ Drive view error:", error);
          if (docData.pdfUrl) {
            console.log("🔗 Using fallback pdfUrl due to Drive API error");
            window.open(docData.pdfUrl, '_blank');
          } else {
            toast.error("Failed to view Drive document. Drive might be unlinked.");
          }
        }
      } else if (docData.pdfUrl) {
        window.open(docData.pdfUrl, '_blank');
      } else if (docData.driveFileId) {
        // Fallback for objects with driveFileId but no pdfSource
        const viewUrl = await getDriveViewUrl(docData.driveFileId);
        window.open(viewUrl, '_blank');
      }
    } catch (error) {
      console.error("❌ View error:", error);
      toast.error("Failed to view document");
    } finally {
      setViewingDocId(null);
    }
  };

  const handleOpenUploadDocModal = async () => {
    setIsActionLoading(true);
    try {
      const driveApi = await import('../../utils/driveApi');
      const driveStatus = await driveApi.checkDriveStatus(true); // force refresh
      if (!driveStatus.linked) {
        toast.error("Google drive not linked by admin, pls contact admin", {
          autoClose: 5000,
          containerId: "bottom-center",
          position: "bottom-center"
        });
        return;
      }
      setShowUploadDocModal(true);
    } catch (error) {
      console.error("Error checking Drive status:", error);
      toast.error("Google drive not linked by admin, pls contact admin", {
        autoClose: 5000,
        containerId: "bottom-center",
        position: "bottom-center"
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      {/* Header */}
      {/* Header Row: Back/Profile on Left, Upload Button on Right */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigate('/client', { state: { currentPage: fromPage, selectedTab: fromTab } })}
            size="small"
            sx={{
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            <ArrowBackIcon sx={{ color: "#212121" }} />
          </IconButton>
          <Typography variant="h5" sx={{
            fontFamily: 'Roboto Slab',
            fontWeight: 550,
            fontSize: '32px',
            lineHeight: '56px',
            color: '#212121'
          }}>
            Profile
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title={!isAccountActive ? 'Account deactivated. Please contact support to renew your subscription.' : ''} arrow>
            <span>
              <Button
                variant="contained"
                startIcon={isActionLoading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenUploadDocModal();
                }}
                disabled={isActionLoading || !isAccountActive}
                sx={{
                  width: 185,
                  height: 48,
                  borderRadius: '8px',
                  p: '16px 12px 16px 8px',
                  gap: '4px',
                  boxShadow: 0,
                  backgroundColor: '#0063F7 !important',
                  color: '#FFFFFF !important',
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '24px',
                  letterSpacing: '0.01em',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#0063F7 !important',
                    color: '#FFFFFF !important',
                    boxShadow: 10
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc !important',
                    color: '#888888 !important',
                  }
                }}
              >
                Upload Documents
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Left-aligned flex row Client card */}
      <Paper
        sx={{
          width: '100%',
          height: 100,
          gap: '16px',
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          boxShadow: 'none',
          backgroundColor: '#0063F7',
          color: '#FFFFFF',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexShrink: 0,
          mb: 0,
        }}
      >
        {/* Avatar, details, buttons stacked */}
        <Avatar sx={{ width: 48, height: 48, bgcolor: '#E0EBFF', color: '#0063F7', mr: 2 }}>
          <PersonIcon sx={{ fontSize: 28 }} />
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 1 }}>
          <Typography sx={{
            fontFamily: 'Roboto',
            fontWeight: 500,
            fontSize: '18px',
            lineHeight: '28px',
            letterSpacing: '0.01em',
            opacity: 1,
            textAlign: 'left',
            mb: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {clientName}
            {customerData?.referredBy && (
              <Typography component="span" sx={{
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '18px',
                lineHeight: '28px',
                letterSpacing: '0.01em',
                opacity: 0.8,
                ml: 2
              }}>
                Referred By: {customerData.referredBy}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={() => setShowEditModal(true)}
              sx={{ color: '#FFFFFF', p: 0.5, ml: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Typography>
          <Typography sx={{
            fontFamily: 'Roboto',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.01em',
            color: '#FFFFFF',
            opacity: 1,
            textAlign: 'left'
          }}>{clientPhone}</Typography>
        </Box>

        {/* Delete and View Documents buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            onClick={(e) => setDocMenuAnchor(e.currentTarget)}
            sx={{
              minWidth: 140,
              height: 40,
              borderRadius: '8px',
              backgroundColor: '#FFFFFF !important',
              color: '#0063F7 !important',
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '14px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
              '&:hover': {
                backgroundColor: '#FFFFFF !important',
                boxShadow: '0 2px 4px rgba(0,0,0,0.35)',
              }
            }}
          >
            <VisibilityIcon sx={{ fontSize: 18 }} />
            View Documents
          </Button>

          <Button
            onClick={() => setDeleteDialogOpen(true)}
            sx={{
              minWidth: 120,
              height: 40,
              borderRadius: '8px',
              backgroundColor: '#DC2626 !important',
              color: '#FFFFFF !important',
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '14px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
              '&:hover': {
                backgroundColor: '#DC2626 !important',
                boxShadow: '0 2px 4px rgba(0,0,0,0.35)',
              }
            }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
            Delete
          </Button>

          {/* Document Views Dropdown Menu */}
          <Menu
            anchorEl={docMenuAnchor}
            open={isDocMenuOpen}
            onClose={() => setDocMenuAnchor(null)}
            PaperProps={{
              sx: {
                width: 300,
                maxHeight: 400,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                borderRadius: '8px',
                mt: 1
              }
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="overline" sx={{ fontWeight: 600, color: '#666' }}>Aadhaar Card</Typography>
              {renderDocumentList('aadhaar', 'Aadhaar Card')}

              <Box sx={{ my: 1, borderBottom: '1px solid #f0f0f0' }} />

              <Typography variant="overline" sx={{ fontWeight: 600, color: '#666' }}>RC Book</Typography>
              {renderDocumentList('rc_book', 'RC Book')}
            </Box>
          </Menu>
        </Box>
      </Paper>



      <Box
        sx={{
          width: '100%',
          background: '#fff',
          borderRadius: '0',
          boxShadow: 'none',
          px: { xs: 2, sm: 3 },
          pt: 2,
          pb: 3,
          height: { xs: 'calc(100vh - 320px)', md: 'calc(100vh - 340px)' },
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          gap: 0,
          overflow: 'hidden'
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          mb: 1,
          gap: 2
        }}>
          <Tabs
            value={selectedCategory}
            onChange={(e, newVal) => {
              setSelectedCategory(newVal);
              setCurrentPage(1);
            }}
            variant="standard"
            size="small"
            indicatorColor="primary"
            sx={{
              width: 'fit-content',
              justifyContent: 'flex-start',
              '& .MuiTab-root': {
                textTransform: 'capitalize',
                fontWeight: 500,
                minWidth: isSmallScreen ? 60 : 80,
                px: isSmallScreen ? 1.5 : 2,
                py: 0.5,
                fontSize: isSmallScreen ? '13px' : 'inherit',
                borderRadius: 1,
                color: '#6B7280',
                '&.Mui-selected': {
                  color: '#0063F7',
                },
                '&:hover': {
                  backgroundColor: '#E0EBFF',
                },
              }
            }}
          >
            {categories.map((c) => (
              <Tab
                key={c}
                label={`${c === "non-motor" ? "Non-motor" : c.charAt(0).toUpperCase() + c.slice(1)} (${categoryCounts[c] || 0})`}
                value={c}
                disableRipple
              />
            ))}
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>Showing:</Typography>
            <Box sx={{
              position: 'relative',
              display: 'flex',
              backgroundColor: '#F3F4F6',
              borderRadius: '20px',
              p: '3px',
              height: '32px',
              width: '160px' // Fixed width for consistent animation
            }}>
              <Box sx={{
                position: 'absolute',
                width: 'calc(50% - 3px)',
                height: 'calc(100% - 6px)',
                backgroundColor: '#FFFFFF',
                borderRadius: '17px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: policyViewType === 'current' ? 'translateX(0)' : 'translateX(100%)',
                zIndex: 0
              }} />
              <ToggleButtonGroup
                value={policyViewType}
                exclusive
                onChange={(e, newVal) => {
                  if (newVal !== null) {
                    setPolicyViewType(newVal);
                    setCurrentPage(1);
                  }
                }}
                sx={{
                  width: '100%',
                  zIndex: 1,
                  border: 'none',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 'none',
                    '&:not(:first-of-type)': {
                      marginLeft: 0,
                    }
                  },
                  '& .MuiToggleButton-root': {
                    flex: 1,
                    borderRadius: '17px',
                    border: 'none',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '13px',
                    color: '#6B7280',
                    lineHeight: 1,
                    backgroundColor: 'transparent !important',
                    transition: 'color 0.3s ease',
                    '&.Mui-selected': {
                      color: '#0063F7',
                      backgroundColor: 'transparent !important',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2) !important',
                    }
                  }
                }}
              >
                <ToggleButton value="current" disableRipple>Current</ToggleButton>
                <ToggleButton value="previous" disableRipple>Previous</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>
        <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 2, mt: -1 }} />

        {loading ? (
          <Typography>Loading policies...</Typography>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: 0,
                px: 0,
                mx: { xs: -2, sm: -3 }, // Extend table beyond container padding
                width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' }, // Compensate for negative margin
                flex: 1,
                overflow: 'auto',
                '& .MuiTableHead-root .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  backgroundColor: 'transparent',
                },
                '& .MuiTableBody-root .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  backgroundColor: 'transparent',
                },
                '& .MuiTable-root': {
                  borderLeft: 'none',
                  borderRight: 'none',
                },
                '& .MuiTableRow-root': {
                  borderRadius: '0 !important',
                },
                '& .MuiTableCell-root': {
                  borderRadius: '0 !important',
                },
                '& .MuiPaper-root': {
                  borderRadius: '0 !important',
                  borderTopLeftRadius: '0 !important',
                  borderTopRightRadius: '0 !important',
                  borderBottomLeftRadius: '0 !important',
                  borderBottomRightRadius: '0 !important',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                  boxShadow: 'none',
                  borderBottom: 'none',
                },
              }}
            >
              <Table
                size="medium"
                aria-label="client policies"
                sx={{
                  border: 'none',
                  borderCollapse: 'collapse',
                  width: '100%',
                  tableLayout: 'fixed',
                  '& th, & td': {
                    border: 'none',
                    px: 0,
                  },
                  '& td:first-of-type, & th:first-of-type': {
                    pl: 4,
                  },
                  '& tr': {
                    borderBottom: '1px solid #E0E0E0',
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ borderBottom: '1px solid #E0E0E0' }}>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: 140 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        {selectedCategory === "motor" ? (
                          <>
                            <DirectionsCarIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 500,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.01em',
                              color: '#4B5563DE',
                              height: 24,
                              opacity: 1,
                              whiteSpace: 'nowrap'
                            }}>
                              Vehicle
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CategoryIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 500,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.01em',
                              color: '#4B5563DE',
                              height: 24,
                              opacity: 1,
                              whiteSpace: 'nowrap'
                            }}>
                              Category
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: 170 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          height: 24,
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Policy Type
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: 170 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <BusinessIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          height: 24,
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Company
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <FlagIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          height: 24,
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Status
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow sx={{ height: 48, verticalAlign: 'middle' }}>
                      <TableCell colSpan={4} align="center" sx={{ px: 0 }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <Typography sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0.02em',
                            color: '#6B7280',
                            mb: 1
                          }}>
                            {policyViewType === 'current' ? 'No policies found.' : 'No previous policies exists.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => navigate(`/policy/${row.id}`, { state: { policy: row.original, fromScreen: 'clientDetail', clientId: customerId, clientFromScreen: 'clients', clientFromPage: fromPage, client: customerData } })}
                        sx={{
                          height: 48,
                          verticalAlign: 'middle',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#E0EBFF !important' }
                        }}
                      >
                        <TableCell
                          sx={{
                            px: 0,
                            width: 140,
                            maxWidth: 140,
                            verticalAlign: 'middle',
                          }}
                        >
                          {selectedCategory === "motor" ? (
                            <Typography
                              sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '24px',
                                letterSpacing: '0.02em',
                                color: '#212121',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: 1,
                                width: '100%',
                              }}
                            >
                              {row.vehicleNumber || "-"}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '24px',
                                letterSpacing: '0.02em',
                                color: '#212121',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: 1,
                                width: '100%',
                              }}
                            >
                              {row.policyCategoryType || "-"}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: 170,
                            maxWidth: 170,
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                            }}
                          >
                            {row.policyType || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: 170,
                            maxWidth: 170,
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                            }}
                          >
                            {row.policyCompany || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: 200,
                            maxWidth: 200,
                            verticalAlign: 'middle',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <StatusBadge status={row.status} size={isSmallScreen ? 'small' : 'normal'} />
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              setPolicyToDelete(row.original);
                              setDeletePolicyDialogOpen(true);
                            }}
                            size="small"
                            sx={{
                              color: '#F04438',
                              '&:hover': {
                                backgroundColor: '#FFF3F2',
                                color: '#D93C32',
                              },
                              ml: 1
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}


                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination moved below container */}
          </>
        )}
      </Box>

      {/* Pagination (moved outside white container) */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 0,
            backgroundColor: '#fff',
            borderRadius: '0',
            boxShadow: 'none',
            pt: 1,
            pb: 1,
          }}
        >
          <Pagination
            count={Math.max(totalPages, 1)}
            page={currentPage}
            onChange={(_, value) => setCurrentPage(value)}
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: '50%',
                border: '1px solid #D1D5DB',
                width: 36,
                height: 36,
                margin: '0 4px',
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: '#E0EBFF',
                },
              },
              '& .MuiPaginationItem-previousNext': {
                borderRadius: 0,
                border: 'none',
                backgroundColor: 'transparent',
                width: 'auto',
                height: 'auto',
                margin: '0 4px',
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              },
              '& .Mui-selected': {
                backgroundColor: '#004DC2 !important',
                color: '#fff !important',
                border: 'none',
                '&:hover': {
                  backgroundColor: '#004DC2 !important',
                },
              },
            }}
          />
        </Box>
      )}

      {showEditModal && (
        <EditProfileModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
          }}
          onSave={handleSaveCustomer}
          initialDetails={editData}
          showAvatar={true}
          disabledFields={[]}
        />
      )}


      {/* Confirmation Dialog for Delete */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
        }}
        PaperProps={{
          sx: {
            width: "600px",
            height: "192px",
            backgroundColor: "#FFFFFF",
            padding: "20px 32px",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "20px",
              lineHeight: "24px",
              color: "#212121",
              textAlign: "left",
              letterSpacing: "0.01em",
              mt: "16px",
            }}
          >
            Are you sure you want to delete this client?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "flex-end",
            padding: 0,
            marginTop: "16px",
            gap: "20px",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteDialogOpen(false);
            }}
            sx={{
              width: "120px",
              height: "44px",
              borderRadius: "8px",
              borderColor: "#F04438",
              color: "#F04438",
              background: "#fff",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              textTransform: "none",
              '&:hover': {
                borderColor: "#F04438",
                background: "#FFF3F2",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isActionLoading}
            onClick={async () => {
              await handleDeleteCustomer();
              setDeleteDialogOpen(false);
            }}
            sx={{
              width: "120px",
              height: "44px",
              borderRadius: "8px",
              backgroundColor: "#F04438 !important",
              color: "#fff !important",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              textTransform: "none",
              boxShadow: "none",
              '&:hover': {
                backgroundColor: "#D93C32 !important",
              },
              '&:disabled': {
                backgroundColor: "#F04438AA !important",
              }
            }}
          >
            {isActionLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Policy Delete Confirmation Dialog */}
      <Dialog
        open={deletePolicyDialogOpen}
        onClose={() => {
          setDeletePolicyDialogOpen(false);
          setPolicyToDelete(null);
        }}
        PaperProps={{
          sx: {
            width: "600px",
            height: "192px",
            backgroundColor: "#FFFFFF",
            padding: "20px 32px",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "20px",
              lineHeight: "24px",
              color: "#212121",
              textAlign: "left",
              letterSpacing: "0.01em",
              mt: "16px",
            }}
          >
            Are you sure you want to delete this policy?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "flex-end",
            padding: 0,
            marginTop: "16px",
            gap: "20px",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setDeletePolicyDialogOpen(false);
              setPolicyToDelete(null);
            }}
            sx={{
              width: "120px",
              height: "44px",
              borderRadius: "8px",
              borderColor: "#F04438",
              color: "#F04438",
              background: "#fff",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              textTransform: "none",
              '&:hover': {
                borderColor: "#F04438",
                background: "#FFF3F2",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isActionLoading}
            onClick={async () => {
              await handleDeletePolicy(policyToDelete);
              setDeletePolicyDialogOpen(false);
              setPolicyToDelete(null);
            }}
            sx={{
              width: "120px",
              height: "44px",
              borderRadius: "8px",
              backgroundColor: "#F04438 !important",
              color: "#fff !important",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              textTransform: "none",
              boxShadow: "none",
              '&:hover': {
                backgroundColor: "#D93C32 !important",
              },
              '&:disabled': {
                backgroundColor: "#F04438AA !important",
              }
            }}
          >
            {isActionLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Policy Modal and Form */}
      <UploadDocumentModal
        isOpen={showUploadDocModal}
        onClose={() => {
          setShowUploadDocModal(false);
          setDocUploadProgress(0);
        }}
        onUpload={handleDocumentUpload}
        loading={isActionLoading}
        progress={docUploadProgress}
      />

      <UploadPolicyModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onExtracted={(data) => {
          setPrefillData(data);
          setShowUploadModal(false);
          setShowPolicyForm(true);
        }}
        user={user}
      />

      {showPolicyForm && (
        <UnifiedPolicyForm
          isOpen={showPolicyForm}
          onClose={() => {
            setShowPolicyForm(false);
            setPrefillData(null);
          }}
          initialCustomerData={{
            name: clientName,
            mobile: clientPhone,
            email: customerData?.email || editData.email || "",
            address: customerData?.address || editData.location || ""
          }}
          prefillData={prefillData}
          pdfFile={null} // Don't pass raw file here, handled via prefillData.rawFile
          mode="create"
          existingCustomerId={customerId}
          agentId={customerData?.agentId || user?.uid}
          onSuccess={handlePolicyUploadSuccess}
        />
      )}

      {/* Document Delete Confirmation Dialog */}
      <Dialog
        open={deleteDocDialogOpen}
        onClose={() => {
          setDeleteDocDialogOpen(false);
          setDocToDelete(null);
        }}
        PaperProps={{
          sx: {
            width: "600px",
            height: "192px",
            backgroundColor: "#FFFFFF",
            padding: "20px 32px",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: "20px",
              lineHeight: "24px",
              color: "#212121",
              textAlign: "left",
              letterSpacing: "0.01em",
              mt: "16px",
            }}
          >
            Are you sure you want to delete this document?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "flex-end",
            padding: 0,
            marginTop: "16px",
            gap: "20px",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteDocDialogOpen(false);
              setDocToDelete(null);
            }}
            sx={{
              width: "120px",
              height: "44px",
              borderRadius: "8px",
              borderColor: "#F04438",
              color: "#F04438",
              background: "#fff",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              textTransform: "none",
              '&:hover': {
                borderColor: "#F04438",
                background: "#FFF3F2",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isActionLoading}
            onClick={async () => {
              if (docToDelete) {
                await handleDeleteDocument(docToDelete.type, docToDelete.id || docToDelete.driveFileId || docToDelete.pdfUrl);
              }
              setDeleteDocDialogOpen(false);
              setDocToDelete(null);
            }}
            sx={{
              width: "120px",
              height: "44px",
              borderRadius: "8px",
              backgroundColor: "#F04438 !important",
              color: "#fff !important",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0.01em",
              textTransform: "none",
              boxShadow: "none",
              '&:hover': {
                backgroundColor: "#D93C32 !important",
              },
              '&:disabled': {
                backgroundColor: "#F04438AA !important",
              }
            }}
          >
            {isActionLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDetail;